// eslint-disable-next-line simple-import-sort/imports
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { STORE_USERNAME_KEY } from "@/app.constant";
import { Button, Footer, PabbitLogo } from "@/components";
import { InputWithClear } from "@/components/form";
import { usePreSignInEmail } from "@/queries";
import { errorToast } from "@/shared";
import { checkTokenExpiration } from "@/shared/requests/auth";
import { setStorage } from "@/shared/store";

interface LoginEmailForm {
  email: string;
}

const emailPattern =
  /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

const LoginEmailPage = () => {
  const navigate = useNavigate();
  const { register, setValue, getValues, watch } = useForm<LoginEmailForm>({
    defaultValues: { email: "" },
  });
  const { mutateAsync: preSignInEmail, isLoading: isPreSignInLoading } = usePreSignInEmail();

  const emailValue = watch("email") ?? "";
  const isValidEmail = useMemo(() => {
    const trimmed = emailValue.trim();
    return trimmed.length > 0 && emailPattern.test(trimmed);
  }, [emailValue]);

  useEffect(() => {
    checkTokenExpiration().then(() => {
      navigate("/");
    });
  }, [navigate]);

  useEffect(() => {
    const stored = window.localStorage.getItem(STORE_USERNAME_KEY)?.trim() ?? "";
    if (stored && emailPattern.test(stored)) {
      setValue("email", stored);
    }
  }, [setValue]);

  const handleGetOtp = async () => {
    const trimmed = emailValue.trim();
    if (!isValidEmail) return;
    window.localStorage.setItem(STORE_USERNAME_KEY, trimmed);
    try {
      await preSignInEmail({ email: trimmed });
      // 携帯ログインと同様、ResendCode がマウント時に初回送信＆60 秒カウントダウンを開始する
      setStorage("isAutoSendedVerifyCode", false);
      navigate("/login-confirm-email");
    } catch (error) {
      errorToast(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <div className="contents">
      <div className="flex flex-1 flex-col bg-white px-8 py-12">
        <PabbitLogo />
        <p className="mt-[30px] text-left text-[16px] font-semibold leading-[140%] tracking-normal text-text-black">
          メールアドレスを入力してください
        </p>
        <div className="mt-[24px]">
          <InputWithClear
            styleType="shadow"
            register={register("email")}
            setValue={setValue}
            getValues={getValues}
            placeholder="例：example@email.com"
            type="email"
            autoComplete="email"
          />
        </div>
        <div className="mt-[40px]">
          <Button
            className="w-full"
            size="large"
            loading={isPreSignInLoading}
            disabled={!isValidEmail}
            onClick={handleGetOtp}
          >
            ワンタイムパスワードを取得
          </Button>
        </div>
        <div className="mt-[40px]">
          <Button
            className="w-full"
            fill="outline"
            nativeType="button"
            size="large"
            onClick={() => navigate("/login")}
          >
            携帯番号でログイン
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LoginEmailPage;
